import React, { useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, CheckCircle2, ListChecks, UploadCloud, MoreHorizontal, LogOut, UserCog, CreditCard } from "lucide-react";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";

const PRIMARY = [
  { to: "/", label: "Home", icon: LayoutDashboard, end: true, testId: "tab-dashboard" },
  { to: "/approve", label: "Approve", icon: CheckCircle2, testId: "tab-approve" },
  { to: "/review", label: "Queue", icon: ListChecks, testId: "tab-review" },
  { to: "/upload", label: "Upload", icon: UploadCloud, testId: "tab-upload" },
];

const Pill = ({ n, testId }) => n > 0 && <span data-testid={testId} className="absolute -right-1 -top-0.5 min-w-[16px] rounded-full bg-amber-500 px-1 text-center text-[10px] font-mono font-semibold leading-4 text-white">{n > 99 ? "99+" : n}</span>;

export const MobileTabBar = ({ more, dups, skipped, onProfile, onBilling, onLogout, isAdmin }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const moreActive = more.some((m) => location.pathname.startsWith(m.to));
  const go = (to) => { setOpen(false); navigate(to); };
  const moreBadge = skipped;
  return (
    <>
      <nav data-testid="mobile-tab-bar" className="fixed inset-x-0 bottom-0 z-40 border-t border-border bg-background/95 backdrop-blur-xl md:hidden" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        <div className="grid h-16 grid-cols-5">
          {PRIMARY.map(({ to, label, icon: Icon, end, testId }) => (
            <NavLink key={to} to={to} end={end} data-testid={testId} className={({ isActive }) => cn("flex flex-col items-center justify-center gap-1 text-[11px] font-medium", isActive ? "text-primary" : "text-muted-foreground")}>
              {({ isActive }) => (<>
                <span className={cn("relative flex h-7 w-12 items-center justify-center rounded-full transition-colors", isActive && "bg-primary/10")}><Icon className="h-5 w-5" />{to === "/review" && <Pill n={dups} testId="tab-review-pill" />}</span>
                {label}
              </>)}
            </NavLink>
          ))}
          <button type="button" onClick={() => setOpen(true)} data-testid="tab-more" className={cn("flex flex-col items-center justify-center gap-1 text-[11px] font-medium", moreActive || open ? "text-primary" : "text-muted-foreground")}>
            <span className={cn("relative flex h-7 w-12 items-center justify-center rounded-full", (moreActive || open) && "bg-primary/10")}><MoreHorizontal className="h-5 w-5" /><Pill n={moreBadge} testId="tab-more-pill" /></span>
            More
          </button>
        </div>
      </nav>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="rounded-t-2xl px-4 pb-[calc(env(safe-area-inset-bottom)+16px)]" data-testid="mobile-more-sheet">
          <SheetHeader className="text-left"><SheetTitle className="text-base">More</SheetTitle></SheetHeader>
          <div className="mt-3 grid grid-cols-3 gap-2">
            {more.map(({ to, label, icon: Icon, testId }) => {
              const active = location.pathname.startsWith(to);
              return (
                <button key={to} type="button" onClick={() => go(to)} data-testid={`${testId}-more`} className={cn("flex flex-col items-center gap-2 rounded-xl border px-2 py-3 text-center text-xs font-medium", active ? "border-primary/40 bg-primary/10 text-primary" : "border-border hover:bg-muted")}>
                  <span className="relative"><Icon className="h-5 w-5" />{to === "/my-week" && <Pill n={skipped} testId="more-skipped-pill" />}</span>
                  <span className="leading-tight">{label}</span>
                </button>
              );
            })}
          </div>
          <div className="mt-4 flex flex-wrap gap-2 border-t border-border pt-3 text-sm">
            <button type="button" onClick={() => { setOpen(false); onProfile(); }} data-testid="more-profile" className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 hover:bg-muted"><UserCog className="h-4 w-4" /> Profile</button>
            {isAdmin && <button type="button" onClick={() => { setOpen(false); onBilling(); }} data-testid="more-billing" className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 hover:bg-muted"><CreditCard className="h-4 w-4" /> Billing</button>}
            <button type="button" onClick={() => { setOpen(false); onLogout(); }} data-testid="more-logout" className="ml-auto inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-red-500 hover:bg-red-500/10"><LogOut className="h-4 w-4" /> Sign out</button>
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};
